export interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
  enabled: boolean;
}

export interface CookieConsent {
  hasResponded: boolean;
  acceptedCategories: string[];
  rejectedCategories: string[];
  timestamp: string;
  version: string;
}

const CONSENT_VERSION = "1.0";
const CONSENT_COOKIE_NAME = "cookie-consent";

export function useCookieConsent() {
  const posthog = usePosthog();
  const metaPixel = useScriptMetaPixel({
    id: useRuntimeConfig().public.scripts.metaPixel.id,
  });

  const cookieCategories = ref<CookieCategory[]>([
    {
      id: "essential",
      name: "Essential",
      description:
        "These cookies are necessary for the website to function and cannot be switched off. They enable core functionality such as security, network management, and accessibility.",
      required: true,
      enabled: true,
    },
    {
      id: "analytics",
      name: "Analytics",
      description:
        "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website performance.",
      required: false,
      enabled: false,
    },
    {
      id: "marketing",
      name: "Marketing",
      description:
        "These cookies are used to track visitors across websites to deliver more relevant advertising and limit the number of times users see the same advertisement.",
      required: false,
      enabled: false,
    },
    {
      id: "preferences",
      name: "Preferences",
      description:
        "These cookies allow the website to remember choices you make and provide enhanced, more personal features such as language preferences and region settings.",
      required: false,
      enabled: false,
    },
  ]);

  const consentData = useCookie<CookieConsent | null>(CONSENT_COOKIE_NAME, {
    default: () => null,
    maxAge: 60 * 60 * 24 * 365, // 1 year
    sameSite: "lax",
    secure: import.meta.env.PROD,
    domain: useRuntimeConfig().public.baseDomain.replace(/:\d+$/, ""),
    path: "/",
    encode: (value) => btoa(JSON.stringify(value)),
    decode: (value) => {
      try {
        return JSON.parse(atob(value));
      } catch {
        return null;
      }
    },
  });

  const hasConsented = computed(() => {
    return consentData.value?.hasResponded === true;
  });

  const showBanner = computed(() => {
    return !hasConsented.value;
  });
  const showPreferences = ref(false);

  const initializeCategories = () => {
    if (consentData.value) {
      cookieCategories.value.forEach((category) => {
        if (category.required) {
          category.enabled = true;
        } else {
          category.enabled = consentData.value!.acceptedCategories.includes(category.id);
        }
      });
    }
  };

  watchEffect(() => {
    if (showPreferences.value) {
      initializeCategories();
    }
  });

  const saveConsent = (acceptedCategories: string[]) => {
    const timestamp = new Date().toISOString();
    const rejectedCategories = cookieCategories.value
      .filter((cat) => !cat.required && !acceptedCategories.includes(cat.id))
      .map((cat) => cat.id);

    consentData.value = {
      hasResponded: true,
      acceptedCategories,
      rejectedCategories,
      timestamp,
      version: CONSENT_VERSION,
    };

    handleTrackingConsent(acceptedCategories);

    showPreferences.value = false;
  };

  const handleTrackingConsent = async (acceptedCategories: string[]) => {
    const analyticsAccepted = acceptedCategories.includes("analytics");
    const marketingAccepted = acceptedCategories.includes("marketing");

    // Handle PostHog consent
    if (posthog) {
      if (!analyticsAccepted || !marketingAccepted) {
        posthog.opt_out_capturing();
        posthog.reset();
      } else {
        posthog.opt_in_capturing();
      }
    }

    if (metaPixel && useRuntimeConfig().public.scripts.metaPixel.id) {
      if (marketingAccepted) {
        metaPixel.proxy.fbq("consent", "grant");
      } else {
        metaPixel.proxy.fbq("consent", "revoke");
      }
    }
  };

  const acceptAll = () => {
    const allCategories = cookieCategories.value.filter((cat) => !cat.required).map((cat) => cat.id);

    cookieCategories.value.forEach((category) => {
      category.enabled = true;
    });

    saveConsent(allCategories);
  };

  const acceptEssential = () => {
    cookieCategories.value.forEach((category) => {
      category.enabled = category.required;
    });

    saveConsent([]);
  };

  const savePreferences = () => {
    const acceptedCategories = cookieCategories.value
      .filter((cat) => !cat.required && cat.enabled)
      .map((cat) => cat.id);

    saveConsent(acceptedCategories);
  };

  const resetConsent = () => {
    consentData.value = null;
    cookieCategories.value.forEach((category) => {
      category.enabled = category.required;
    });
  };

  const openPreferences = () => {
    showPreferences.value = true;
  };

  const checkConsentStatus = () => {
    if (hasConsented.value) {
      initializeCategories();
      if (consentData.value) {
        handleTrackingConsent(consentData.value.acceptedCategories);
      }
    }
  };

  const isCategoryAccepted = (categoryId: string) => {
    const category = cookieCategories.value.find((cat) => cat.id === categoryId);
    return category?.enabled || false;
  };

  const getConsentSummary = () => {
    if (!consentData.value) return null;

    return {
      timestamp: new Date(consentData.value.timestamp).toLocaleDateString(),
      acceptedCategories: consentData.value.acceptedCategories,
      rejectedCategories: consentData.value.rejectedCategories,
      version: consentData.value.version,
    };
  };

  return {
    cookieCategories,
    showBanner,
    showPreferences,
    hasConsented,

    acceptAll,
    acceptEssential,
    savePreferences,
    resetConsent,
    openPreferences,
    checkConsentStatus,
    isCategoryAccepted,
    getConsentSummary,

    saveConsent,
  };
}
